import type AssistantToolCall from '../../framework/models/AssistantToolCall';
import type Assistant from '../models/Assistant';
import type AssistantViewController from '../components/AssistantView/AssistantViewController';

export type AgentSuggestions = {
  title: string;
  prompt: string;
  prefix?: string;
  icon?: string;
}[];
export type AgentParams = {
  projectId: RecordId | undefined;
  [key: string]: any;
};
export type AgentInstances = Record<string, any>;

type AgentContentComponent = React.ComponentType<{
  assistant: Assistant;
  mimeType: string;
  content: string;
}>;

// Name of the default agent to fall back to if no agent is specified.
const DEFAULT_AGENT_NAME = 'DefaultAgent';

// Base class for all agents.
class Agent {
  // Name of the Ruby class implementing the agent on the server.
  static name = 'Override in sub-class';

  static publicName = 'Override in sub-class';

  constructor(
    protected controller: AssistantViewController,
    protected readonly agentParams: AgentParams,
    private readonly agentInstances: AgentInstances
  ) {}

  name(): string {
    return (this.constructor as typeof Agent).name;
  }

  publicName(): string {
    return (this.constructor as typeof Agent).publicName;
  }

  // Determine if the agent is currently available for use based on the available
  // agent params and instances.
  static available(
    agentParams: AgentParams,
    agentInstances: AgentInstances
  ): boolean {
    return true;
  }

  // Suggestions for prompts that will be shown on the suggestions panel. The
  // key is the text for the button the user will see. The value is the prompt
  // text that will be pre-filled into the prompt editor when the button is
  // clicked.
  getSuggestions(): AgentSuggestions {
    return [];
  }

  // Override this method to return a custom component for rendering a specific
  // mime type. Return nothing to get the default rendering.
  componentForMimeType(
    assistant: Assistant,
    mimeType: string
  ): AgentContentComponent | void {}

  // Give the agent an opportunity to modify the data provided to a tool call
  beforeApproveToolCall(
    toolCall: AssistantToolCall,
    data?: Record<string, unknown>
  ): Record<string, unknown> | undefined {
    return data;
  }

  // Give the agent an opportunity to react to a tool call when it is approved
  // or rejected. Return false to prevent the action.
  approveToolCall(toolCall: AssistantToolCall, data?: Record<string, unknown>) {
    return true;
  }
  rejectToolCall(toolCall: AssistantToolCall) {
    return true;
  }

  // Give the agent a way to provide additional data for a tool call.
  // Return false skip providing data. Otherwise return an object with the
  // data to be provided.
  provideToolCallData(toolCall: AssistantToolCall): Object | false {
    return false;
  }

  // Give the agent an opportunity to react to a tool call when it is created.
  processToolCall(toolCall: AssistantToolCall): void {}

  // Display additional actions for the tool call.
  getToolActions(toolCall: AssistantToolCall): React.ReactNode | null {
    return null;
  }

  // Allows an agent to provide an action that will be called when the content of the toolcall is clicked.
  onContentAction(toolCall: AssistantToolCall): (() => void) | undefined {
    return undefined;
  }

  // Register an agent with the system so that it can be looked up by name.
  static register(agent: typeof Agent) {
    this.agentRegistry[agent.name] = agent;
  }

  // Internal use methods =================================================

  // The registry of all agents. This is used to look up agents by name.
  static agentRegistry: Record<string, typeof Agent> = {};

  // Look up an agent by name.
  static lookup(name: string): typeof Agent | undefined {
    return this.agentRegistry[name];
  }

  static getAllAgents(): (typeof Agent)[] {
    return Object.values(this.agentRegistry);
  }
}

export default Agent;
export { DEFAULT_AGENT_NAME };
